import { useState } from "react";
import {
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  Column,
  EmptyState,
  HtImage,
  IconButton,
  Row,
  SkeletonBox,
  Text,
} from "@hightouch/htui";
import type { AmpLibraryFileFragment } from "src/graphql";

export type SlidesPreviewProps = {
  file: AmpLibraryFileFragment;
  selectedSlideId?: string | null;
  onSelectSlide?: (slideId: string) => void;
};

export function SlidesPreview({
  file,
  selectedSlideId,
  onSelectSlide,
}: SlidesPreviewProps) {
  const [selectedSlide, setSelectedSlide] = useState(0);
  const slides = file.slides;
  const urls = slides?.slideUrls ?? [];
  const ids = slides?.slideIds ?? [];
  const selectedIndex = selectedSlideId
    ? ids.indexOf(selectedSlideId)
    : selectedSlide;
  const missingSelection = Boolean(selectedSlideId) && selectedIndex === -1;
  const index = selectedSlideId
    ? selectedIndex
    : Math.max(0, Math.min(selectedIndex, urls.length - 1));
  const selectSlide = (next: number) => {
    setSelectedSlide(next);
    if (ids[next]) onSelectSlide?.(ids[next]);
  };
  const generating = file.generationStatus === "generating";
  const failed = file.generationStatus === "failed";
  const hasDeck = Boolean(slides?.downloadUrl);

  return (
    <Column bg="white" gap={0}>
      <Box position="relative" width="100%" sx={{ aspectRatio: "16 / 9" }}>
        {urls[index] ? (
          <HtImage
            src={urls[index]}
            alt={`${file.name || "Presentation"}, slide ${index + 1}`}
            width="100%"
            height="100%"
            objectFit="contain"
            draggable={false}
            showError
          />
        ) : generating ? (
          <SkeletonBox width="100%" height="100%" borderRadius="none" />
        ) : (
          <EmptyState
            variant="minimal"
            height="100%"
            message={
              missingSelection
                ? "This slide was removed. Choose another slide to continue."
                : failed
                  ? "Couldn’t create presentation"
                  : hasDeck
                    ? "Preview unavailable. Download the presentation to view it."
                    : "Preview unavailable"
            }
          />
        )}
        {((generating && !hasDeck) || failed) && (
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            px={3}
            py={2}
            bg="white"
            role="status"
          >
            <Text size="sm" color="text.secondary">
              {generating
                ? "Creating presentation…"
                : hasDeck
                  ? "Couldn’t update presentation. Ask the agent to try again."
                  : "Ask the agent to try again."}
            </Text>
          </Box>
        )}
      </Box>
      <Column
        className="nodrag nopan"
        pointerEvents="auto"
        position="relative"
        zIndex={1}
        p={3}
        minHeight={14}
        gap={2}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        {urls.length > 0 && (
          <Row justify="center" align="center" gap={2}>
            <IconButton
              aria-label="Previous slide"
              icon={ChevronLeftIcon}
              shape="circle"
              size="sm"
              variant="tertiary"
              isDisabled={index <= 0}
              onClick={() => selectSlide(index - 1)}
            />
            <Text size="sm" aria-live="polite">
              {missingSelection
                ? "Slide removed"
                : `Slide ${index + 1} of ${slides?.slideCount ?? urls.length}`}
            </Text>
            <IconButton
              aria-label="Next slide"
              icon={ChevronRightIcon}
              shape="circle"
              size="sm"
              variant="tertiary"
              isDisabled={index >= urls.length - 1}
              onClick={() => selectSlide(index + 1)}
            />
          </Row>
        )}
        {slides?.slideCount &&
          slides.slideCount > urls.length &&
          urls.length > 0 && (
            <Text size="sm" color="text.secondary">
              Preview shows the first {urls.length} slides.
            </Text>
          )}
      </Column>
    </Column>
  );
}
